import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import SEOHelmet from '../components/SEOHelmet';

const VendorListPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter and search states
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [selectedLocation, setSelectedLocation] = useState(searchParams.get('location') || 'all');
  const [priceRange, setPriceRange] = useState(searchParams.get('priceRange') || 'all');
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'nameAZ');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');

  // Mock data - In real app, this would come from API
  const mockVendors = [
    {
      id: '1',
      name: 'Royal Photography Studio',
      category: 'photographers',
      location: 'Mumbai',
      startingPrice: 25000,
      phone: '+91-9876543210',
      image: 'https://images.unsplash.com/photo-1606800052052-a08af7148866?w=400&h=300&fit=crop',
      isSponsored: true,
      subscription: {
        isSponsored: true,
        type: 'premium',
        startDate: new Date(),
        endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
      },
      verified: true,
      description: 'Professional wedding photography with 10+ years experience',
      services: ['Pre-wedding', 'Wedding Photography', 'Videography', 'Drone Shots']
    },
    {
      id: '2',
      name: 'Grand Palace Banquet',
      category: 'wedding-venues',
      location: 'Delhi',
      startingPrice: 150000,
      phone: '+91-9876543211',
      image: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=400&h=300&fit=crop',
      isSponsored: false,
      subscription: {
        isSponsored: false,
        type: null,
        startDate: null,
        endDate: null
      },
      verified: true,
      description: 'Luxury wedding venue with capacity for 500+ guests',
      services: ['AC Banquet Hall', 'Parking', 'Catering', 'Decoration']
    },
    {
      id: '3',
      name: 'Spice Garden Catering',
      category: 'catering-services',
      location: 'Bangalore',
      startingPrice: 800,
      phone: '+91-9876543212',
      image: 'https://images.unsplash.com/photo-1555244162-803834f70033?w=400&h=300&fit=crop',
      isSponsored: true,
      subscription: {
        isSponsored: true,
        type: 'gold',
        startDate: new Date(),
        endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
      },
      verified: true,
      description: 'Multi-cuisine catering with authentic Indian and continental dishes',
      services: ['Indian Cuisine', 'Chinese', 'Continental', 'Live Counters']
    },
    {
      id: '4',
      name: 'Elite Wedding Planners',
      category: 'wedding-planners',
      location: 'Chennai',
      startingPrice: 50000,
      phone: '+91-9876543213',
      image: 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=400&h=300&fit=crop',
      isSponsored: false,
      subscription: {
        isSponsored: false,
        type: null,
        startDate: null,
        endDate: null
      },
      verified: true,
      description: 'Complete wedding planning and coordination services',
      services: ['Full Planning', 'Vendor Coordination', 'Day-of Coordination', 'Destination Weddings']
    },
    {
      id: '5',
      name: 'Blooming Flowers',
      category: 'florists',
      location: 'Pune',
      startingPrice: 15000,
      phone: '+91-9876543214',
      image: 'https://images.unsplash.com/photo-1490750967868-88aa4486c946?w=400&h=300&fit=crop',
      isSponsored: false,
      subscription: {
        isSponsored: false,
        type: null,
        startDate: null,
        endDate: null
      },
      verified: true,
      description: 'Fresh flower arrangements and wedding decorations',
      services: ['Bridal Bouquet', 'Venue Decoration', 'Car Decoration', 'Garlands']
    },
    {
      id: '6',
      name: 'Beats & Rhythm DJ',
      category: 'music-dj',
      location: 'Mumbai',
      startingPrice: 20000,
      phone: '+91-9876543215',
      image: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=300&fit=crop',
      isSponsored: true,
      subscription: {
        isSponsored: true,
        type: 'premium',
        startDate: new Date(),
        endDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000)
      },
      verified: true,
      description: 'Professional DJ services with latest sound systems',
      services: ['DJ Services', 'Sound System', 'Lighting', 'Live Music']
    }
  ];

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'photographers', label: 'Photographers' },
    { value: 'wedding-venues', label: 'Wedding Venues' },
    { value: 'catering-services', label: 'Catering Services' },
    { value: 'wedding-planners', label: 'Wedding Planners' },
    { value: 'florists', label: 'Florists' },
    { value: 'music-dj', label: 'Music & DJ' },
    { value: 'decorators', label: 'Decorators' },
    { value: 'makeup-artists', label: 'Makeup Artists' }
  ];

  const locations = [
    { value: 'all', label: 'All Locations' },
    { value: 'Mumbai', label: 'Mumbai' },
    { value: 'Delhi', label: 'Delhi' },
    { value: 'Bangalore', label: 'Bangalore' },
    { value: 'Chennai', label: 'Chennai' },
    { value: 'Pune', label: 'Pune' },
    { value: 'Hyderabad', label: 'Hyderabad' },
    { value: 'Kolkata', label: 'Kolkata' }
  ];

  const priceRanges = [
    { value: 'all', label: 'All Budgets' },
    { value: '0-25000', label: 'Under ₹25,000' },
    { value: '25000-50000', label: '₹25,000 - ₹50,000' },
    { value: '50000-100000', label: '₹50,000 - ₹1,00,000' },
    { value: '100000-200000', label: '₹1,00,000 - ₹2,00,000' },
    { value: '200000+', label: 'Above ₹2,00,000' }
  ];

  const sortOptions = [
    { value: 'nameAZ', label: 'Name: A to Z' },
    { value: 'nameZA', label: 'Name: Z to A' },
    { value: 'priceLow', label: 'Price: Low to High' },
    { value: 'priceHigh', label: 'Price: High to Low' }
  ];

  useEffect(() => {
    // Simulate API call
    setLoading(true);
    setTimeout(() => {
      setVendors(mockVendors);
      setLoading(false);
    }, 1000);
  }, [mockVendors]);

  useEffect(() => {
    let filtered = [...vendors];

    // Apply category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(vendor => vendor.category === selectedCategory);
    }

    // Apply location filter
    if (selectedLocation !== 'all') {
      filtered = filtered.filter(vendor => vendor.location === selectedLocation);
    }

    // Apply price range filter
    if (priceRange !== 'all') {
      const [min, max] = priceRange.split('-').map(p => p.replace('+', ''));
      filtered = filtered.filter(vendor => {
        if (priceRange === '200000+') return vendor.startingPrice >= 200000;
        return vendor.startingPrice >= parseInt(min) && vendor.startingPrice <= parseInt(max);
      });
    }

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(vendor => 
        vendor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        vendor.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        vendor.services.some(service => service.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Apply sorting - sponsored vendors first, then by selected criteria
    filtered.sort((a, b) => {
      // Check if either vendor is sponsored
      const aSponsored = a.isSponsored || a.subscription?.isSponsored || false;
      const bSponsored = b.isSponsored || b.subscription?.isSponsored || false;
      
      // If one is sponsored and the other isn't, sponsored comes first
      if (aSponsored && !bSponsored) return -1;
      if (!aSponsored && bSponsored) return 1;
      
      // If both are sponsored or both are not sponsored, sort by selected criteria
      switch (sortBy) {
        case 'nameAZ':
          return a.name.localeCompare(b.name);
        case 'nameZA':
          return b.name.localeCompare(a.name);
        case 'priceLow':
          return a.startingPrice - b.startingPrice;
        case 'priceHigh':
          return b.startingPrice - a.startingPrice;
        default:
          return a.name.localeCompare(b.name); // Default to A-Z sorting
      }
    });

    setFilteredVendors(filtered);
  }, [vendors, selectedCategory, selectedLocation, priceRange, searchQuery, sortBy]);

  const updateSearchParams = (key, value) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (value === 'all' || value === '') {
      newSearchParams.delete(key);
    } else {
      newSearchParams.set(key, value);
    }
    setSearchParams(newSearchParams);
  };



  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }, (_, index) => (
                <div key={index} className="bg-white rounded-lg shadow-sm p-4">
                  <div className="h-48 bg-gray-200 rounded mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Wedding Vendors - Ceremonzy",
    "description": `Browse ${filteredVendors.length} verified wedding vendors on Ceremonzy`,
    "url": "https://ceremonzy.com/vendors",
    "mainEntity": {
      "@type": "ItemList",
      "numberOfItems": filteredVendors.length,
      "itemListElement": filteredVendors.slice(0, 10).map((vendor, index) => ({
        "@type": "LocalBusiness",
        "position": index + 1,
        "name": vendor.name,
        "description": vendor.description,
      }))
    }
  };

  return (
    <>
      <SEOHelmet
        title={`Wedding Vendors - Browse ${filteredVendors.length} Verified Vendors | Ceremonzy`}
        description={`Discover ${filteredVendors.length} verified wedding vendors on Ceremonzy. Find photographers, caterers, decorators, venues and more for your perfect ceremony. Book trusted professionals today.`}
        keywords="wedding vendors list, ceremony vendors, wedding photographers, wedding caterers, wedding venues, wedding decorators, verified vendors, Ceremonzy vendors"
        url="/vendors"
        structuredData={structuredData}
      />
      <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-6 sm:mb-8 text-center sm:text-left">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              Ceremony Vendors
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              Found {filteredVendors.length} verified vendors for your ceremony
            </p>
          </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="space-y-4 sm:space-y-0 sm:grid sm:grid-cols-2 lg:grid-cols-5 sm:gap-4">
            {/* Search */}
            <div>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search vendors..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    updateSearchParams('search', e.target.value);
                  }}
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery('');
                      updateSearchParams('search', '');
                    }}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <select
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  updateSearchParams('category', e.target.value);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              >
                {categories.map(category => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Location Filter */}
            <div>
              <select
                value={selectedLocation}
                onChange={(e) => {
                  setSelectedLocation(e.target.value);
                  updateSearchParams('location', e.target.value);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              >
                {locations.map(location => (
                  <option key={location.value} value={location.value}>
                    {location.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Price Range Filter */}
            <div>
              <select
                value={priceRange}
                onChange={(e) => {
                  setPriceRange(e.target.value);
                  updateSearchParams('priceRange', e.target.value);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              >
                {priceRanges.map(range => (
                  <option key={range.value} value={range.value}>
                    {range.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort By */}
            <div>
              <select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  updateSearchParams('sortBy', e.target.value);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              >
                {sortOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Vendor Grid */}
        {filteredVendors.length === 0 ? (
          <div className="text-center py-8 sm:py-12 mx-4 sm:mx-0">
            <div className="text-4xl sm:text-6xl mb-4">🔍</div>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">No vendors found</h3>
            <p className="text-sm sm:text-base text-gray-600">Try adjusting your filters or search criteria</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 px-2 sm:px-0">
            {filteredVendors.map((vendor) => (
              <div
                key={vendor.id}
                className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden"
              >
                
                {/* Vendor Image */}
                <div className="relative">
                  <img
                    src={vendor.images?.coverImage || vendor.image || 'https://images.unsplash.com/photo-1560472355-536de3962603?w=400&h=300&fit=crop'}
                    alt={vendor.name}
                    className="w-full h-48 object-cover"
                  />
                  {vendor.verified && (
                    <div className="absolute top-3 right-3 bg-green-500 text-white rounded-full p-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>

                {/* Vendor Details */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 line-clamp-1">
                      {vendor.name}
                    </h3>
                    <a 
                      href={`tel:${vendor.phone}`}
                      className="flex items-center text-green-600 hover:text-green-700 transition-colors duration-200"
                    >
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                      </svg>
                      <span className="text-sm font-medium">Call</span>
                    </a>
                  </div>

                  <div className="flex items-center text-sm text-gray-600 mb-2">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    {vendor.location}
                  </div>

                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {vendor.description}
                  </p>

                  {/* Services */}
                  <div className="flex flex-wrap gap-1 mb-4">
                    {vendor.services.slice(0, 3).map((service, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-pink-100 text-pink-700 text-xs rounded-full"
                      >
                        {service}
                      </span>
                    ))}
                    {vendor.services.length > 3 && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                        +{vendor.services.length - 3} more
                      </span>
                    )}
                  </div>

                  {/* Phone Number */}
                  <div className="flex items-center justify-between mb-3">
                    <a 
                      href={`tel:${vendor.phone}`}
                      className="flex items-center text-blue-600 hover:text-blue-700 transition-colors duration-200 bg-blue-50 px-3 py-2 rounded-lg"
                    >
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                      </svg>
                      <span className="text-sm font-medium">{vendor.phone}</span>
                    </a>
                  </div>

                  {/* Price and CTA */}
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-lg font-bold text-gray-900">
                        ₹{vendor.startingPrice.toLocaleString()}
                      </span>
                      <span className="text-sm text-gray-600"> onwards</span>
                    </div>
                    <Link
                      to={`/vendor/${vendor.id}`}
                      className="px-4 py-2 bg-pink-600 text-white text-sm font-medium rounded-lg hover:bg-pink-700 transition-colors duration-200"
                    >
                      View Details
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Load More */}
        {filteredVendors.length > 0 && (
          <div className="text-center mt-12">
            <button className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors duration-200">
              Load More Vendors
            </button>
          </div>
        )}
        </div>
      </div>
    </>
  );
};

export default VendorListPage;